import { NodeLiveOutputTypes } from "../../plugins/dataflow/model/utilities/node";
import { parseKeyValueData } from "./serial-protocol";
import { deviceProtocol } from "../../plugins/dataflow/model/utilities/device-capabilities";
function log(message) {
    // eslint-disable-next-line no-console
    console.log(`[SerialDevice] ${message}`);
}
export class SerialDevice {
    constructor() {
        Object.defineProperty(this, "localBuffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connectChangeStamp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastConnectMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "serialNodesCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "serialModalShown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceFamily", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // The active write transport (Web Serial or WebUSB). writeLine routes here.
        Object.defineProperty(this, "activeTransport", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // The connecting tile's channels, recorded at connect so inbound data can be parsed.
        Object.defineProperty(this, "channels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        /**
         * True when we think the connected Web Serial board will raise the browser
         * connect/disconnect events. It drives connect-button physical-connection UI. Only ever set
         * by the Web Serial path — stays false for a WebUSB (Spiker:bit) connection.
         */
        Object.defineProperty(this, "raisesWebSerialConnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.localBuffer = "";
    }
    setSerialNodesCount(n) {
        this.serialNodesCount = n;
    }
    // TODO, revise this so it is more clear how its used
    updateConnectionInfo(timeStamp, status) {
        this.connectChangeStamp = timeStamp;
        this.lastConnectMessage = status;
        localStorage.setItem("last-connect-message", status);
    }
    setRaisesWebSerialConnect(raises) {
        this.raisesWebSerialConnect = raises;
    }
    isConnected() {
        return this.activeTransport != null;
    }
    // Called by a transport (Web Serial or WebUSB) to become the active write path, receive
    // inbound data centrally, and mark the store connected.
    setActiveDevice(deviceFamily, transport, channels) {
        // Start each session with an empty parse buffer so a partial line left by a prior
        // connection can't combine with new data into a spurious message.
        this.localBuffer = "";
        this.activeTransport = transport;
        this.deviceFamily = deviceFamily;
        this.channels = channels;
        transport.onData = (chunk) => { if (this.activeTransport === transport)
            this.receive(chunk); };
        transport.onDisconnect = () => { if (this.activeTransport === transport)
            this.clearActiveDevice(); };
        this.updateConnectionInfo(Date.now(), "connect");
    }
    clearActiveDevice() {
        this.activeTransport = undefined;
        this.deviceFamily = undefined;
        this.localBuffer = "";
        // Reset so a prior Web Serial session's value can't stick to a later WebUSB device, which
        // never raises the browser connect/disconnect events this flag gates the connect-button UI on.
        this.raisesWebSerialConnect = false;
        this.updateConnectionInfo(Date.now(), "disconnect");
    }
    // Central inbound router: dispatch a serial chunk to the parser for the connected
    // device's protocol, using the recorded channels.
    receive(chunk) {
        const protocol = deviceProtocol(this.deviceFamily);
        if (protocol === "keyValue")
            this.handleKeyValueStreamObj(chunk, this.channels);
        if (protocol === "radioHub")
            this.handleRadioHubStreamObj(chunk, this.channels);
    }
    handleRadioHubStreamObj(value, channels) {
        this.localBuffer += value;
        // [sc]   signal or control
        // [abcd] which micro:bit
        // [rth]  relay, temp, humidity
        const pattern = /([sc]{1})([abcd]{1})([rth]{1})([0-9.]+)\s{0,}[\r][\n]/g;
        let match;
        do {
            match = pattern.exec(this.localBuffer);
            if (!match)
                break;
            const [fullMatch, signalType, microbitId, element, reading] = match;
            this.localBuffer = this.localBuffer.substring(match.index + fullMatch.length);
            const targetChannelId = `${element}-${microbitId}`;
            const targetChannel = channels.find((c) => {
                return c.channelId === targetChannelId;
            });
            if (targetChannel && signalType === "s") {
                if (["h", "t"].includes(element)) {
                    // handle message from a humidity or temperature sensor
                    if (isFinite(Number(reading))) {
                        targetChannel.value = Number(reading);
                    }
                    targetChannel.lastMessageReceivedAt = Date.now();
                }
                if (["r"].includes(element)) {
                    // handle message about relays state
                    targetChannel.relaysState = reading.split('').map(s => Number(s));
                    targetChannel.lastMessageReceivedAt = Date.now();
                }
            }
        } while (match);
    }
    handleKeyValueStreamObj(value, channels) {
        this.localBuffer = parseKeyValueData(this.localBuffer + value, channels);
    }
    writeLine(line) {
        if (this.activeTransport) {
            this.activeTransport.write(line);
        }
        else {
            log("Port closed, skipping write");
        }
    }
    writeToOutForMicroBitRelayHub(data, hubId, relayType) {
        const ri = NodeLiveOutputTypes.filter((ot) => ot.name === relayType)[0].relayIndex;
        const controlMessage = `c${hubId}${ri}${data}`;
        this.writeLine(controlMessage);
    }
    writeToOutForBBGripper(n, liveOutputType) {
        const outputConfig = NodeLiveOutputTypes.find(o => o.name === liveOutputType);
        if (this.isConnected() && (outputConfig === null || outputConfig === void 0 ? void 0 : outputConfig.angleBase) !== undefined) {
            const percent = n / 100;
            const openTo = Math.round(outputConfig.angleBase - (percent * outputConfig.sweep));
            this.writeLine(openTo.toString());
        }
    }
    writeToOutForServo(n, liveOutputType) {
        const outputConfig = NodeLiveOutputTypes.find(o => o.name === liveOutputType);
        if (this.isConnected() && (outputConfig === null || outputConfig === void 0 ? void 0 : outputConfig.angleOffset) !== undefined) {
            const scaledAngle = (outputConfig.angleScale * n) + outputConfig.angleOffset;
            const roundedScaled = Math.round(scaledAngle);
            this.writeLine(roundedScaled.toString());
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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